import { getCurrencyScaleAndFormatterForValues, makeScaledValueFormatter } from "helpers/formatters/currency-scale"
import { LOADING_CELL_TEXT } from "helpers/compare-composition-table"
import type { RowUnit } from "helpers/compare-composition-table"
import { GMV_SUPPORTED_TYPES } from "helpers/comparison"
import { ERROR, OK } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"

export const GROSS_MARKET_VALUE_LABEL = "Gross Market Value"
export const MARKET_CAPITALIZATION_LABEL = "Market Capitalization"

const MARKET_CAPITALIZATION_SCALE = "M"
const NOT_APPLICABLE_TEXT = "--"
const MISSING_VALUE_TEXT = "-"

export interface CompositionColumn {
  type?: string
  noData?: boolean
  isUnmodeled?: boolean
}

export interface CompositionDailyData {
  summary?: {
    gmv?: unknown
    equity?: unknown
    averageMarketCap?: unknown
  }
}

type DailyDataEntry = CompositionDailyData | null | undefined

interface DollarRowInput {
  columns: readonly CompositionColumn[]
  detailsData: readonly DailyDataEntry[]
  currency?: string | null
}

interface ScaledDollarRowInput extends DollarRowInput {
  statuses: readonly (DataStatus | undefined)[]
}

export interface DollarRow {
  label: string
  fn: (item: DailyDataEntry, type?: string) => string
  unit?: RowUnit
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value)
}

function isNonZeroNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value !== 0
}

function isGrossMarketValueType(type: string | undefined): boolean {
  return type !== undefined && GMV_SUPPORTED_TYPES.includes(type)
}

function getDisplayedIndexes(
  columns: readonly CompositionColumn[],
  isCounted: (column: CompositionColumn) => boolean = () => true,
): number[] {
  return columns.flatMap((column, index) => !column.noData && !column.isUnmodeled && isCounted(column) ? [index] : [])
}

function isStillLoading(dailyData: DailyDataEntry, status: DataStatus | undefined): boolean {
  return !dailyData && status !== OK && status !== ERROR
}

function readFiniteValues(
  indexes: readonly number[],
  detailsData: readonly DailyDataEntry[],
  readValue: (dailyData: DailyDataEntry) => unknown,
): number[] {
  return indexes.map(index => readValue(detailsData[index])).filter(isFiniteNumber)
}

const readGrossMarketValue = (dailyData: DailyDataEntry): unknown => dailyData?.summary?.gmv
const readReferenceEquity = (dailyData: DailyDataEntry): unknown => dailyData?.summary?.equity
const readAverageMarketCap = (dailyData: DailyDataEntry): unknown => dailyData?.summary?.averageMarketCap

export function getRawGrossMarketValue(dailyData: DailyDataEntry, type?: string): number | null {
  const value = readGrossMarketValue(dailyData)
  return isGrossMarketValueType(type) && isFiniteNumber(value) ? value : null
}

export function getRawReferenceEquity(dailyData: DailyDataEntry): number | null {
  const value = readReferenceEquity(dailyData)
  return isNonZeroNumber(value) ? value : null
}

export function getRawAverageMarketCap(dailyData: DailyDataEntry): number | null {
  const value = readAverageMarketCap(dailyData)
  return isFiniteNumber(value) ? value : null
}

export function buildGrossMarketValueRow({ columns, detailsData, statuses, currency }: ScaledDollarRowInput): DollarRow {
  const label = GROSS_MARKET_VALUE_LABEL
  const eligibleIndexes = getDisplayedIndexes(columns, ({ type }) => isGrossMarketValueType(type))
  const values = readFiniteValues(eligibleIndexes, detailsData, readGrossMarketValue)

  if (eligibleIndexes.some(index => isStillLoading(detailsData[index], statuses[index]))) {
    return { label, fn: (_item, type) => isGrossMarketValueType(type) ? LOADING_CELL_TEXT : NOT_APPLICABLE_TEXT }
  }

  if (values.length === 0) {
    return { label, fn: (_item, type) => isGrossMarketValueType(type) ? MISSING_VALUE_TEXT : NOT_APPLICABLE_TEXT }
  }

  const { currencyScale, formatter: formatScaledValue } = getCurrencyScaleAndFormatterForValues(values)

  return {
    label,
    fn: (item, type) => {
      if (!isGrossMarketValueType(type)) return NOT_APPLICABLE_TEXT
      const value = readGrossMarketValue(item)
      return isFiniteNumber(value) ? formatScaledValue(value) : MISSING_VALUE_TEXT
    },
    unit: { currencyScale, currency },
  }
}

export function buildReferenceEquityRow({ label, columns, detailsData, statuses, currency }: ScaledDollarRowInput & { label: string }): DollarRow {
  const displayedIndexes = getDisplayedIndexes(columns)
  const values = readFiniteValues(displayedIndexes, detailsData, readReferenceEquity).filter(isNonZeroNumber)

  if (displayedIndexes.some(index => isStillLoading(detailsData[index], statuses[index]))) {
    return { label, fn: () => LOADING_CELL_TEXT }
  }

  if (values.length === 0) {
    return { label, fn: () => NOT_APPLICABLE_TEXT }
  }

  const { currencyScale, formatter: formatScaledValue } = getCurrencyScaleAndFormatterForValues(values)

  return {
    label,
    fn: (item) => {
      const value = readReferenceEquity(item)
      return isNonZeroNumber(value) ? formatScaledValue(value) : NOT_APPLICABLE_TEXT
    },
    unit: { currencyScale, currency },
  }
}

export function buildMarketCapitalizationRow({ columns, detailsData, currency }: DollarRowInput): DollarRow {
  const label = MARKET_CAPITALIZATION_LABEL
  const displayedIndexes = getDisplayedIndexes(columns)
  const values = readFiniteValues(displayedIndexes, detailsData, readAverageMarketCap)

  if (values.length === 0) {
    return { label, fn: () => MISSING_VALUE_TEXT }
  }

  const formatScaledValue = makeScaledValueFormatter(MARKET_CAPITALIZATION_SCALE)

  return {
    label,
    fn: (item) => {
      const value = readAverageMarketCap(item)
      return isFiniteNumber(value) ? formatScaledValue(value) : MISSING_VALUE_TEXT
    },
    unit: { currencyScale: MARKET_CAPITALIZATION_SCALE, currency },
  }
}
